import React, {FC, useState, useRef} from "react"
import styled from "styled-components"

import Flex from "@irati/ui/src/Flex"
import {H2} from "@irati/ui/src/Typography"
import GenericModal from "@irati/ui/src/GenericModal"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {Checkbox, Input, ValidationButton} from "@irati/ui/src/FormItem"
import {Email, Firstname, Lastname} from "@irati-web/fields/user"
import Middlename from "@irati-web/fields/user/Middlename"
import validation from "@irati/utils/validation"
import Form from "@irati/ui/src/Form"
import {AreYouSurePopUp} from "@irati-web/admin/src/components/AreYouSurePopUp"

export const StyledModal = styled(GenericModal)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
    .modal-dialog {
      position: fixed;
      bottom: 0px;
  `}
`

const StyledForm = styled(Form)`
  .form-group {
    margin-bottom: 0;
  }
`

export type SBSUser = {
  firstName: string
  lastName: string
  emailAddress: string
  middleName: string | null
  companyName: string
}

interface Props {
  user: SBSUser
  show: boolean
  onHide: () => void
  onConfirm: (updatedUser: SBSUser) => void
  hideCompanyName?: boolean
}

const EditDetailsModal: FC<Props> = ({user, show, onHide, onConfirm, hideCompanyName}) => {
  const [firstName, setFirstName] = useState(user.firstName)
  const [middleName, setMiddleName] = useState(user.middleName)
  const [lastName, setLastName] = useState(user.lastName)
  const [emailAddress, setEmailAddress] = useState(user.emailAddress)
  const [checked, setChecked] = useState(false)
  const {formatMessage} = useIntl()
  const areYouSureRef = useRef(null)
  const agreeSchema = validation.bool().oneOf([true], "platform.defaultValidation.required")

  const handleEditDetailsClose = () => {
    if (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      middleName !== user.middleName ||
      emailAddress !== user.emailAddress
    ) {
      if (areYouSureRef?.current) {
        areYouSureRef?.current.toggle()
      }
    } else {
      onHide()
    }
  }

  const handleUpdate = () => {
    onConfirm({firstName, middleName, lastName, emailAddress})
  }

  return (
    <>
      <StyledModal hideActions show={show} customWidth={900} onHide={handleEditDetailsClose}>
        <StyledForm>
          <H2 marginBottom={1} textAlign="center">
            <FormattedMessage id="platform.selfSignUp.welcome.editDetailsButton" />
          </H2>
          <Flex direction="column">
            <div>
              <Firstname value={firstName} onChange={setFirstName} />
              <Middlename value={middleName} onChange={setMiddleName} />
              <Lastname value={lastName} onChange={setLastName} />
              <Email value={emailAddress} onChange={setEmailAddress} />
              {!hideCompanyName && (
                <Input
                  name="company"
                  label={formatMessage({id: "platform.fields.company.label"})}
                  value={user.companyName}
                  validation={validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
                  disabled
                />
              )}
              <Checkbox
                name="confirmedPrimaryContactDetails"
                checked={checked}
                onChange={() => setChecked(!checked)}
                validation={agreeSchema}
                tabIndex={0}>
                <FormattedMessage id="platform.selfSignUp.welcome.editDetailsCheckbox" />
              </Checkbox>
            </div>
          </Flex>
          <Flex justifyContent="flex-end">
            <ValidationButton primary mobileFull marginBottom={2} marginTop={2} onClick={handleUpdate}>
              <FormattedMessage id="platform.selfSignUp.welcome.updateInfoButton" />
            </ValidationButton>
          </Flex>
        </StyledForm>
      </StyledModal>
      <AreYouSurePopUp
        ref={areYouSureRef}
        onSubmit={onHide}
        text={formatMessage({id: "platform.selfSignUp.welcome.popUp.text"})}
        cancelButtonText={formatMessage({id: "platform.selfSignUp.welcome.popUp.goBack"})}
        buttonText={formatMessage({id: "platform.selfSignUp.welcome.popUp.close"})}
      />
    </>
  )
}
export default EditDetailsModal
